import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Plus, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead, PremiumBanner } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";

export default function Timers() {
  const { guildId, guild } = useGuild();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", message: "", interval_minutes: 60, channel: "general" });
  const locked = false;
  const load = () => api.get(`/guilds/${guildId}/timers`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [guildId]);

  const add = async () => {
    if (!form.name || !form.message) return toast.error("Name and message required");
    try { await api.post(`/guilds/${guildId}/timers`, form); toast.success("Timer created"); setForm({ name: "", message: "", interval_minutes: 60, channel: "general" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/guilds/${guildId}/timers/${id}`); load(); };

  return (
    <div>
      <PageHead title="Timers" desc="Post recurring scheduled announcements automatically." />
      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
          <h3 className="font-head font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-volt" /> New timer</h3>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Name</label>
            <input data-testid="timer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Message</label>
            <textarea data-testid="timer-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm" /></div>
          <div className="flex gap-3">
            <div className="flex-1"><label className="text-xs uppercase tracking-wider text-zinc-500">Every (min)</label>
              <input type="number" min="1" value={form.interval_minutes} onChange={(e) => setForm({ ...form, interval_minutes: +e.target.value })} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
            <div className="flex-1"><label className="text-xs uppercase tracking-wider text-zinc-500">Channel</label>
              <ChannelSelect guildId={guildId} value={form.channel} onChange={(v) => setForm({ ...form, channel: v })} testid="timer-channel" placeholder="— select a channel —" /></div>
          </div>
          <button data-testid="timer-add" onClick={add} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Create timer</button>
        </div>
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Timers ({items.length})</h3>
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} data-testid="timer-row" className="flex items-center gap-3 bg-black/30 border border-zinc-800 rounded-lg p-3">
                <Clock className="w-5 h-5 text-volt shrink-0" />
                <div className="flex-1 min-w-0"><div className="font-medium">{it.name}</div><div className="text-xs text-zinc-500 truncate">{it.message}</div></div>
                <span className="text-xs font-mono text-zinc-400">every {it.interval_minutes}m · #{it.channel}</span>
                <button data-testid="timer-delete" onClick={() => del(it.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {items.length === 0 && <p className="text-zinc-600 text-sm">No timers yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
